<template>
  <div>
    <div class="user-attributes-filter-wrapper-collapsed" v-if="collapsed">
      <div class="filter-title clickable" :title="labels.filters">
        <i class="mdi mdi-loading mdi-spin" v-if="filterLoading"></i>
        <i class="mdi mdi-filter" v-else></i>
      </div>
      <div class="filter-title clickable" :title="labels.reports">
        <i class="mdi mdi-loading mdi-spin" v-if="reportsLoading"></i>
        <i class="mdi mdi-flash" v-else></i>
      </div>
    </div>
    <div class="user-attributes-filter-wrapper" @click.stop v-else>
      <div class="filter-title">
        <i class="mdi mdi-loading mdi-spin" v-if="filterLoading"></i>
        <i class="mdi mdi-filter" v-else></i>
        <span>{{ labels.filters }}</span>
        <i
          v-if="showFilters"
          class="clickable mdi mdi-chevron-up"
          @click="showFilters = false"
        ></i>
        <i
          v-else
          class="clickable mdi mdi-chevron-down"
          @click="showFilters = true"
        ></i>
      </div>
      <b-collapse class="filter-collapse" v-model="showFilters">
        <div class="filter-select-items">
          <div
            class="filter-select-item"
            v-for="userAttribute in userAttributes"
            :key="`user-attribute-${userAttribute.id}`"
          >
            <label class="custom-input-label">
              {{ userAttribute.label }}
            </label>
            <treeselect
              ref="treeselect"
              name="user-attributes-select"
              :placeholder="labels.select"
              :clearAllText="labels.cleanSelect"
              :options="userAttribute.values"
              :multiple="multiple"
              :clearable="clearable"
              :searchable="searchable"
              :open-on-click="openOnClick"
              :open-on-focus="openOnFocus"
              :close-on-select="closeOnSelect"
              :always-open="alwaysOpen"
              @input="userAttribute.selected = $event"
              :value="userAttribute.selected"
            >
            </treeselect>
          </div>
          <div
            class="not-found"
            v-if="!filterLoading && userAttributes.length === 0"
          >
            {{ labels.notFoundAttributes }}
          </div>
          <div class="text-center" v-else>
            <b-button
              class="mt-2 mb-4"
              variant="secondary"
              size="sm"
              :disabled="requestLoading"
              @click="generateReport()"
            >
              <i class="mdi mdi-loading mdi-spin" v-if="requestLoading"></i>
              <i class="mdi mdi-filter" v-else></i>
              <span class="ml-1">{{ labels.requestReport }}</span>
            </b-button>
          </div>
        </div>
      </b-collapse>
      <div class="filter-title">
        <i class="mdi mdi-loading mdi-spin" v-if="reportsLoading"></i>
        <i class="mdi mdi-flash" v-else></i>
        <span>{{ labels.reports }}</span>
        <i
          v-if="showReports"
          class="clickable mdi mdi-chevron-up"
          @click="showReports = false"
        ></i>
        <i
          v-else
          class="clickable mdi mdi-chevron-down"
          @click="showReports = true"
        ></i>
      </div>
      <b-collapse class="filter-collapse" v-model="showReports">
        <div class="user-attributes-reports" v-if="reportsLoading">
          <p class="loading">{{ labels.loadingReport }}</p>
        </div>
        <div class="user-attributes-reports" v-else>
          <div
            class="report-item clickable"
            :class="{ active: isReportSelected(report) }"
            v-for="(report, index) in reports"
            :key="`user-attribute-report-${report.key}`"
          >
            <label
              class="user-attribute-report-label"
              :title="`${index + 1}# - ${getTitle(report)}`"
              @click="selectReport(report)"
            >
              <i
                v-if="report.status === 'done'"
                class="mdi mdi-check text-success mr-2"
              >
              </i>
              <b-badge
                v-if="report.status === 'processing'"
                pill
                variant="secondary"
                class="processing-badge"
              >
                <i class="mdi mdi-loading mdi-spin processing-report"></i>
                {{ labels.processingReport }}
              </b-badge>
              <b-badge
                v-if="isReportSelected(report)"
                pill
                variant="tertiary"
                class="unselect-badge"
              >
                {{ labels.unselectReport }}
              </b-badge>
              <span class="bold">{{ index + 1 }}#</span> -
              {{ getTitle(report) }}
            </label>
          </div>
          <div class="not-found" v-if="!reportsLoading && reports.length === 0">
            {{ labels.notfoundReport }}
          </div>
          <div class="text-center" v-else>
            <b-button
              class="mt-2 mb-4"
              variant="link"
              size="sm"
              @click="loadReports(false)"
            >
              <i class="mdi mdi-flash"></i>
              <span>{{ labels.reloadReport }}</span>
            </b-button>
          </div>
        </div>
      </b-collapse>
    </div>
    <b-modal
      id="modal-conflict"
      :title="labels.filters"
      cancel-variant="outline-primary"
      ref="modal-unauthorized"
      header-bg-variant="danger"
    >
      <p class="txt-20">{{ errorMessage }}</p>
      <template v-slot:modal-footer>
        &nbsp;
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import Treeselect from '@riophae/vue-treeselect'
import '@riophae/vue-treeselect/dist/vue-treeselect.css'

export default {
  props: {
    groupOid: {
      type: String,
      required: true,
    },
    collapsed: {
      type: Boolean,
      required: true,
    },
  },
  components: { Treeselect },
  data() {
    return {
      multiple: true,
      clearable: true,
      searchable: false,
      openOnClick: true,
      openOnFocus: true,
      closeOnSelect: true,
      alwaysOpen: false,
      showFilters: false,
      showReports: true,
      errorMessage: null,
    }
  },
  computed: {
    ...mapGetters({
      resources: 'resources',
      selectedGroup: 'user/selectedGroup',
      userAttributes: 'userAttributes/filterUserAttributes',
      filterLoading: 'userAttributes/filterLoading',
      reportsLoading: 'userAttributes/reportsLoading',
      reports: 'userAttributes/reportsUserAttributes',
      selectedReport: 'userAttributes/selectedReport',
      requestLoading: 'userAttributes/requestLoading',
    }),

    labels() {
      return this.resources.component.userAttributesFilter
    },
  },
  methods: {
    selectFilter(report) {
      const reportUserAttributes = report.key.split('|')
      reportUserAttributes.shift() // remove key first position - group id

      const mapUserAttributes = reportUserAttributes.reduce((map, item) => {
        const [key, value] = item.split(':')
        if (!map[key]) {
          map[key] = []
        }
        map[key].push(value)
        return map
      }, {})

      this.userAttributes.forEach(u => {
        u.selected = mapUserAttributes[u.id] ? mapUserAttributes[u.id] : []
      })
    },
    generateReport() {
      this.errorMessage = null

      const selected = this.userAttributes
        .filter(a => a.selected && a.selected.length > 0)
        .map(a => ({ id: a.id, selected: a.selected }))

      const userAttributes = selected.reduce((attributes, attribute) => {
        attribute.selected.forEach(v =>
          attributes.push({ key: attribute.id, value: v })
        )
        return attributes
      }, [])

      if (!userAttributes.length) {
        return
      }

      this.$store.dispatch('userAttributes/setRequestLoading', true)
      const response = this.$store.dispatch(
        'userAttributes/requestUserAttributesReport',
        {
          id: this.groupOid,
          userAttributes,
        }
      )
      response
        .then(() => {
          this.loadReports(false)
          this.$store.dispatch('userAttributes/setRequestLoading', false)
        })
        .catch(error => {
          this.$store.dispatch('userAttributes/setRequestLoading', false)
          const {
            status,
            data: { message },
          } = error.response
          if (status === 400) {
            this.errorMessage = message
            this.$refs['modal-unauthorized'].show()
          }
        })
    },
    selectReport(report) {
      // check selection removal
      if (this.selectedReport && this.selectedReport.key === report.key) {
        this.$store.dispatch('userAttributes/selectReport', null)
        this.$emit('loadUserAttributeReport', { id: report.key.split('|')[0] })
        this.userAttributes.forEach(u => (u.selected = []))
        return
      }

      this.selectFilter(report)

      this.$store.dispatch('userAttributes/selectReport', report)
      this.$emit('loadUserAttributeReport', report)
    },
    getTitle(report) {
      const createdAt = this.$moment(report.createdAt).format('DD/MM/YYYY')

      const userAttributes = report.userAttributes
        .map(a => `${a.key} - ${a.value}`)
        .join(', ')

      return `${createdAt} - ${userAttributes}`
    },
    isReportSelected(report) {
      const selectReport = this.selectedReport || false
      return selectReport ? selectReport.id === report.id : false
    },
    loadReports(unselect) {
      this.$store.dispatch('userAttributes/loadUserAttributesReports', {
        groupId: this.groupOid,
        unselect,
      })
    },
  },
  created() {
    if (!this.collapsed) {
      const selectedReport = this.selectedReport || false

      // select filters
      if (selectedReport) {
        this.selectFilter(selectedReport)
      } else {
        this.$store.dispatch('userAttributes/loadUserAttributesFilter', {
          hidden: true,
        })
      }

      const isSelected =
        selectedReport && selectedReport.key.split('|')[0] === this.groupOid

      this.loadReports(!isSelected)
    }
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.user-attributes-filter-wrapper-collapsed {
  width: 50px;

  .filter-title {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-top: 1px solid map-get($theme-colors, 'gray07');
    border-bottom: 1px solid map-get($theme-colors, 'gray07');
    height: 50px;
    align-items: center;
    font-weight: bold;

    i {
      margin-left: 18px;
    }
  }
}

.user-attributes-filter-wrapper {
  width: 350px;
  // padding: 10px;
  // background-color: red;

  .user-attributes-reports {
    font-size: 0.8em;

    .loading,
    .not-found {
      margin-top: 15px;
      margin-left: 15px;
      margin-bottom: 15px;
    }

    .report-item {
      padding-top: 5px;
      border-bottom: 1px solid map-get($theme-colors, 'gray07');

      &.active {
        background-color: map-get($theme-colors, 'gray08');
      }

      &:hover {
        box-shadow: 0 5px 18px 0 rgba(0, 0, 0, 0.2);
        // background-color: map-get($theme-colors, 'gray06');
      }

      .user-attribute-report-label {
        margin-left: 15px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        cursor: pointer;
        max-width: 340px;
        overflow: hidden;
        text-overflow: ellipsis;

        .processing-badge,
        .unselect-badge {
          i {
            margin-right: 2px;
          }

          margin-right: 10px;
          text-transform: uppercase;
        }

        .processing-report {
          margin-right: 10px;
        }
      }
    }
  }

  .filter-title {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-top: 1px solid map-get($theme-colors, 'gray07');
    border-bottom: 1px solid map-get($theme-colors, 'gray07');
    height: 50px;
    align-items: center;
    font-weight: bold;

    i {
      margin-left: 25px;
      margin-right: 25px;
    }
  }

  .filter-select-items {
    .not-found {
      font-size: 0.8em;
      margin-top: 15px;
      margin-left: 15px;
      margin-bottom: 15px;
    }

    .filter-select-item {
      margin: 10px;
      margin-bottom: 20px;

      .custom-input-label {
        position: relative;
        color: map-get($theme-colors, 'primary') !important;
        font-size: 12px;
        top: 0px;
        left: 20px !important;
      }

      /deep/ .vue-treeselect {
        .vue-treeselect__menu {
          border-radius: 30px !important;
          overflow-x: auto;
        }

        .vue-treeselect__checkbox--checked {
          border-color: map-get($theme-colors, 'primary') !important;
          background: map-get($theme-colors, 'primary') !important;
        }

        .vue-treeselect__control {
          border-radius: 30px !important;
          height: 48px;
        }

        .vue-treeselect__placeholder {
          margin: 7px;
          font-size: 10pt;
        }

        .vue-treeselect__multi-value-item {
          border-radius: 30px !important;
          background-color: map-get($theme-colors, 'gray08');
          color: map-get($theme-colors, 'primary') !important;

          .vue-treeselect__value-remove {
            color: map-get($theme-colors, 'primary') !important;
          }

          .vue-treeselect__multi-value-label {
            white-space: normal;
          }
        }

        .vue-treeselect__label-container {
          &:hover {
            .vue-treeselect__checkbox--unchecked {
              border-color: map-get($theme-colors, 'primary');
            }
          }

          .vue-treeselect__label {
            overflow: inherit;
          }
        }

        .vue-treeselect__option--selected {
          background-color: map-get($theme-colors, 'gray08');
        }
      }
    }
  }
}
</style>
